import React, { useState, useEffect } from 'react';
import { Card, Divider, TablePagination } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useServiceClient } from 'src/hooks/useServiceClient';
import useAuth from 'src/hooks/useAuth';
import { FiltersHeader } from 'src/components/Search/components/FiltersHeader';
import { FiltersSummary, FiltersTabs } from 'src/components/Search';
import CustomPagination from 'src/components/CustomPagination';
import { UserPlus } from 'react-feather';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Box, Table, TableHead, TableCell, TableRow, Checkbox, makeStyles } from '@material-ui/core';
import { VisibleRows } from 'src/components/Tables/VisibleRows';
import {
    ServiceClientsTableBody,
    SERVICE_CLIENTS_TABLE_COLUMNS,
} from 'src/components/ServiceClients';
import { ServiceClientAdvancedFilters } from './AdvancedFilters';
import { ServiceClientsBulkActionsHeader } from './ServiceClientsBulkActionsHeader';
import { getDefaultServiceClientFilters } from './getDefaultServiceClientFilters';
import {
    NO_DISPLAYABLE_SERVICE_CLIENT_FILTERS,
    SERVICE_CLIENT_CREATE_PATH,
    SERVICE_CLIENT_TABS_FILTERS,
    SERVICE_CLIENTS_VISIBLE_FIELDS,
    SERVICE_CLIENTS_VISIBLE_ROWS_LOCAL_STORAGE_KEY,
} from './constants';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import { SERVICE_CLIENT_AUTH } from 'src/constants/permissions';
import { DEVELOPER_LEVEL_ACCESS } from 'src/components/access';
import { IServiceClientFilterValues } from './types';

const useStyles = makeStyles((theme) => ({
    tableHead: {
        backgroundColor: theme.palette.background.paper,
        position: 'sticky',
        top: 0,
        zIndex: 2,
    },
}));

interface ResultsProps {
    values: IServiceClientFilterValues;
    setValues: React.Dispatch<React.SetStateAction<IServiceClientFilterValues>>;
    loadInformation: () => void;
}

export const Results = ({
    values,
    setValues,
    loadInformation,
}: ResultsProps) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { user } = useAuth();
    const {
        clients,
        total,
        loading: loadingClients,
    } = useServiceClient();

    const [isAdvancedSearchOpen, setAdvancedSearchOpen] = useState(false);
    const [modalRowOpen, setModalRowOpen] = useState(false);
    const [selectedServiceClientIds, setSelectedServiceClientIds] = useState<string[]>([]);
    const [visibleRows, setVisibleRows] = useState<Record<string, boolean>>({
        name: true,
        email: true,
        phone: true,
        store: true,
        assigned: true,
        vehicle: true,
        createdAt: true,
    });

    useEffect(() => {
        const saved = localStorage.getItem(SERVICE_CLIENTS_VISIBLE_ROWS_LOCAL_STORAGE_KEY);
        if (saved) {
            try {
                const parsed: Record<string, boolean> = JSON.parse(saved);
                setVisibleRows((prev) => ({ ...prev, ...parsed }));
            } catch {
                // keep default
            }
        }
    }, []);

    useEffect(() => {
        localStorage.setItem(
            SERVICE_CLIENTS_VISIBLE_ROWS_LOCAL_STORAGE_KEY,
            JSON.stringify(visibleRows)
        );
    }, [visibleRows]);

    const visibleColumns = SERVICE_CLIENTS_TABLE_COLUMNS.filter(
        (key: string) => visibleRows[key]
    );

    const hasCreatePermission = isPermissionGranted({
        permissions: user?.permissions || {},
        permission: SERVICE_CLIENT_AUTH,
        crud: 'create',
    });

    const handlePageChange = (
        _: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => setValues((prev) => ({ ...prev, page: newPage }));

    const handleLimitChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) =>
        setValues((prev) => ({
            ...prev,
            limit: parseInt(e.target.value, 10),
        }));

    const handleSelectAllServiceClients = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedServiceClientIds(
            event.target.checked ? (clients ?? []).map((c) => c._id) : []
        );
    };

    const handleSelectOneServiceClient = (
        _: React.ChangeEvent<HTMLInputElement>,
        id: string
    ) => {
        setSelectedServiceClientIds((prev) =>
            prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
        );
    };

    const selectedSome =
        selectedServiceClientIds.length > 0 &&
        selectedServiceClientIds.length < (clients?.length ?? 0);
    const selectedAll =
        (clients?.length ?? 0) > 0 &&
        selectedServiceClientIds.length === (clients?.length ?? 0);

    const enableBulkOperations = selectedServiceClientIds.length > 0;
    const canUseBulkActions = user?.accessLevel === DEVELOPER_LEVEL_ACCESS;

    const renderCustomPagination = () => (
        <CustomPagination
            page={values.page}
            handlePageChange={handlePageChange}
            limit={values.limit}
            count={total ?? 0}
        />
    );

    return (
        <>
            <ServiceClientAdvancedFilters
                open={isAdvancedSearchOpen}
                setOpen={setAdvancedSearchOpen}
                onApply={(f) => setValues(f)}
                values={values}
            />

            <VisibleRows
                fields={SERVICE_CLIENTS_VISIBLE_FIELDS}
                visibleRows={visibleRows}
                setVisibleRows={setVisibleRows}
                open={modalRowOpen}
                setOpen={setModalRowOpen}
            />

            <Card elevation={0}>
                <FiltersTabs
                    tabs={SERVICE_CLIENT_TABS_FILTERS}
                    defaultFilters={getDefaultServiceClientFilters()}
                    values={values}
                    setValues={setValues}
                    disable={loadingClients || enableBulkOperations}
                />
                <Divider />

                {canUseBulkActions && enableBulkOperations ? (
                    <ServiceClientsBulkActionsHeader />
                ) : (
                    <FiltersHeader
                    advancedSearch={{
                        setAdvancedSearchOpen: () => setAdvancedSearchOpen(true),
                        isEnabled: true,
                    }}
                    queryField={{
                        searchLabel: 'serviceClients.Search',
                        isEnabled: !loadingClients,
                    }}
                    create={{
                        createLabel: hasCreatePermission
                            ? 'serviceClients.Create'
                            : '',
                        createPath: hasCreatePermission
                            ? SERVICE_CLIENT_CREATE_PATH
                            : '',
                        icon: <UserPlus />,
                    }}
                    loadInformation={{
                        loadInformation,
                        isEnabled: !loadingClients,
                    }}
                    setModalRowOpen={setModalRowOpen}
                    setValues={(v) => setValues(v)}
                    values={{
                        ...values,
                        isTabsEnabled: values.isTabsEnabled ?? false,
                        tabValue: values.tabValue ?? '',
                    }}
                />
                )}

                <Divider />

                {values.isSearching && (
                    <FiltersSummary
                        filters={values}
                        onClear={() =>
                            setValues(getDefaultServiceClientFilters())
                        }
                        noDisplayableFilters={
                            NO_DISPLAYABLE_SERVICE_CLIENT_FILTERS
                        }
                    />
                )}

                <PerfectScrollbar>
                    <Box style={{ height: '70vh' }}>
                        <Table>
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    {canUseBulkActions && (
                                        <TableCell padding="checkbox" data-testid="service-clients-table-head-checkbox">
                                            <Checkbox
                                                checked={selectedAll}
                                                indeterminate={selectedSome}
                                                onChange={handleSelectAllServiceClients}
                                            />
                                        </TableCell>
                                    )}
                                    {visibleColumns.map((key: string) => (
                                        <TableCell key={key}>
                                            {String(t(`serviceClients.${key}`))}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <ServiceClientsTableBody
                                clients={clients}
                                loading={loadingClients}
                                limit={values.limit}
                                visibleRows={visibleRows}
                                selectedIds={canUseBulkActions ? selectedServiceClientIds : undefined}
                                onSelectAll={canUseBulkActions ? handleSelectAllServiceClients : undefined}
                                onSelectOne={canUseBulkActions ? handleSelectOneServiceClient : undefined}
                            />
                        </Table>
                    </Box>
                </PerfectScrollbar>

                <TablePagination
                    component="div"
                    count={total ?? 0}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleLimitChange}
                    page={values.page}
                    rowsPerPage={values.limit}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    ActionsComponent={renderCustomPagination}
                />
            </Card>
        </>
    );
};
